import { useState, useCallback, createContext, useContext } from "react";
import { ENV } from "env";
import { decodeJWT } from "utils";

const AnalyticsContext = createContext();

/**
 * @name AnalyticsProvider
 * @function
 * @param {Object} config - Configuration for AnalyticsInstance
 * @param {AnalyticsInstance} config.analyticsInstance
 * @param {JSX.Element|JSX.Element[]} config.children
 * @returns {JSX.Element}
 * @description Adds additional functionality around an AnalyticsInstance
 * (created from the "Analytics" function) like storing jwt/ctx/debug
 * from 'identify' call to be used later in page and track calls.  Also
 * stores a 'startTime' from when the component was loaded (essentially, page load)
 * to then be able to calculate how far an event happend from the page load.
 * This can be used to help make optimization improvements in the future.
 */
export const AnalyticsProvider = ({ children, analyticsInstance }) => {
  const [startTime] = useState(Date.now());
  const [jwt, setJWT] = useState(null);
  const [ctx, setCTX] = useState(null);
  const [debug, setDebug] = useState(null);
  
  const getTimeSinceStart = useCallback(() => {
    const now = Date.now();
    return now - startTime;
  }, [startTime]);

  const identify = useCallback(
    (sessionJWT, sessionCtx, debugFn = null, data = {}) => {
      const contextMsg = "[ANALYTICS]: IDENTIFY";
      const userData = decodeJWT(sessionJWT.jwt);
      setJWT(sessionJWT);
      setCTX(sessionCtx);
      if (debugFn) setDebug(() => debugFn);
      const { account_id, exp, username, ...rest } = userData; // username usually contains email address.  Do we want that in our analytics data???
      if (!account_id) {
        console.error(`${contextMsg} - missing required data`);
        return;
      }

      const payload = {
        ...rest,
        account_id,
        msFromStart: getTimeSinceStart(),
        flow: sessionCtx?.flow || null,
        wrappingUrl: sessionCtx?.intCtx || null,
        environment: ENV,
        ...data, // spreading data here to allow overriding set values above
      };

      analyticsInstance.identify({
        title: contextMsg,
        account_id,
        payload,
        debug: debugFn,
        jwt: sessionJWT,
      });
    },
    [getTimeSinceStart] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const page = useCallback(
    (categoryStr, nameStr, data = {}) => {
      const contextMsg = "[ANALYTICS]: PAGE";
      const payload = {
        msFromStart: getTimeSinceStart(),
        flow: ctx?.flow || null,
        wrappingUrl: ctx?.intCtx || null,
        environment: ENV,
        ...data, // spreading data here to allow overriding set values above
      };

      analyticsInstance.page({
        title: contextMsg,
        categoryStr,
        nameStr,
        payload,
        jwt,
        debug,
      });
    },
    [jwt, ctx, debug, getTimeSinceStart] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const track = useCallback(
    (eventStr, data = {}) => {
      const title = "[ANALYTICS]: TRACK";
      const payload = {
        msFromStart: getTimeSinceStart(),
        flow: ctx?.flow || null,
        wrappingUrl: ctx?.intCtx || null,
        environment: ENV,
        ...data, // spreading data here to allow overriding set values above
      };

      analyticsInstance.track({
        title,
        eventStr,
        payload,
        debug,
        jwt,
      });
    },
    [jwt, ctx, debug, getTimeSinceStart] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AnalyticsContext.Provider value={{ identify, page, track }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => useContext(AnalyticsContext);
